import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// === GENERATE TASK ===
export function generateTask() {
  const nonReducible = [2, 3, 5, 6, 7, 10, 11, 13];
  const root = nonReducible[Math.floor(Math.random() * nonReducible.length)];
  const number = Math.floor(Math.random() * 9) + 1;

  // true -> √root jest pierwsze (A = √root, B = number)
  // false -> number jest pierwsze (A = number, B = √root)
  const sqrtFirst = Math.random() < 0.5;

  const left = sqrtFirst
    ? `(\\sqrt{${root}} + ${number})`
    : `(${number} + \\sqrt{${root}})`;

  const right = sqrtFirst
    ? `(\\sqrt{${root}} - ${number})`
    : `(${number} - \\sqrt{${root}})`;

  const expression = `${left}${right}`;

  // (A + B)(A - B) = A^2 - B^2
  // A zawsze jest "pierwszy" w obu nawiasach (sqrtFirst kontroluje, kto jest A)
  const computedValue = sqrtFirst ? root - number ** 2 : number ** 2 - root;
  const expected = String(computedValue);

  // do wyjaśnienia: pokaż A^2 - B^2 w czytelnej postaci
  const aLatex = sqrtFirst ? `\\sqrt{${root}}` : `${number}`;
  const bLatex = sqrtFirst ? `${number}` : `\\sqrt{${root}}`;

  const a2Latex = sqrtFirst ? `(\\sqrt{${root}})^2` : `${number}^2`;
  const b2Latex = sqrtFirst ? `${number}^2` : `(\\sqrt{${root}})^2`;

  const rawSquaresLine = `${a2Latex} - ${b2Latex}`;
  const numericSquaresLine = sqrtFirst
    ? `${root} - ${number ** 2}`
    : `${number ** 2} - ${root}`;

  return {
    type: "DifferenceOfSquaresWithRootTask",
    data: {
      root,
      number,
      expression,
      expected,
      sqrtFirst,
      aLatex,
      bLatex,
      rawSquaresLine,
      numericSquaresLine,
    },
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={data.expression} />
    </p>
  );
}

// === INPUT ===
// ✅ taki sam typ inputa jak w innych zadaniach z pierwiastkami (dla spójności / zmyłki)
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={userAnswer || ""}
      onChange={onChange}
      disabled={disabled}
      wide={true}
    />
  );
}

// === ANSWER ===
export function renderAnswer({ data }) {
  return <InlineMath math={data.expected} />;
}

// === EXPLANATION ===
export function renderExplanation({ data }) {
  const { expression, aLatex, bLatex, rawSquaresLine, numericSquaresLine, expected } =
    data;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru różnicy kwadratów:
      </p>
      <BlockMath math="(a + b)(a - b) = a^2 - b^2" />

      <p>
        <strong>Krok 2:</strong> Tu <InlineMath math={`a = ${aLatex}`} /> oraz{" "}
        <InlineMath math={`b = ${bLatex}`} />, więc:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${expression} = ${rawSquaresLine}`} />
      </div>

      <p>
        <strong>Krok 3:</strong> Obliczamy:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${expression} = ${numericSquaresLine} = ${expected}`} />
      </div>
    </div>
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userAnswer) {
  try {
    const normalize = (str) =>
      String(str ?? "")
        .replace(/\s+/g, "")
        .replace(/−/g, "-")
        .replace(/^\+/, "") // akceptuj wiodący plus
        .toLowerCase()
        .trim();

    const expectedNorm = normalize(data.expected);
    const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw;
    const userNorm = normalize(raw);

    return {
      isCorrect: userNorm === expectedNorm,
      expected: data.expected,
    };
  } catch (e) {
    console.error("Błąd w checkAnswer:", e);
    return {
      isCorrect: false,
      expected: data.expected,
    };
  }
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw;
  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={String(raw)} />;
}
